// Conversion helpers
// 100 cents = 1 carat = 200 milligrams = 0.2 grams
const centsToCarats = (cents) => cents / 100;
const centsToGrams = (cents) => (cents / 100) * 0.2;

export const calculatePrice = (product, metalRates) => {
  // Find rate matching both metal_type AND metal_carat (purity)
  const rate = metalRates.find(r => 
    r.metal_type.toLowerCase() === product.metal_type.toLowerCase() &&
    r.purity.toLowerCase() === (product.metal_carat || '22k').toLowerCase()
  );
  
  // Fallback: if exact purity not found, try to find any rate for the metal type
  const fallbackRate = rate || metalRates.find(r => 
    r.metal_type.toLowerCase() === product.metal_type.toLowerCase()
  );
  
  if (!fallbackRate) return null;

  // Metal price (net weight)
  const basePrice = product.weight * fallbackRate.rate_per_gram;
  
  // Calculate making charges based on type (percentage or fixed)
  let makingCharges;
  if (product.making_charges_type === 'fixed') {
    makingCharges = product.making_charges_percent;
  } else {
    makingCharges = basePrice * (product.making_charges_percent / 100);
  }
  
  // Apply discount ONLY on making charges
  const discountPercent = product.discount_percent || 0;
  const discountAmount = makingCharges * (discountPercent / 100);
  const makingChargesAfterDiscount = makingCharges - discountAmount;
  
  // Stone price (grams input)
  const stoneWeight = product.stone_weight || 0;
  const stoneAmount = product.stone_amount || 0;
  
  // Diamond price (cents input)
  const diamondCents = product.diamond_cents || 0;
  const diamondCarats = centsToCarats(diamondCents);
  const diamondGrams = centsToGrams(diamondCents);
  const diamondAmount = product.diamond_amount || 0;
  const diamondQuality = product.diamond_quality || '';
  
  // Precious Stone price (cents input)
  const preciousStoneCents = product.precious_stone_cents || 0;
  const preciousStoneCarats = centsToCarats(preciousStoneCents);
  const preciousStoneGrams = centsToGrams(preciousStoneCents);
  const preciousStoneAmount = product.precious_stone_amount || 0;
  
  // Other Material (steel, rubber, etc.)
  const otherMaterialType = product.other_material_type || '';
  const otherMaterialWeight = product.other_material_weight || 0;
  const otherMaterialAmount = product.other_material_amount || 0;
  
  // Calculate total extras
  const totalExtras = stoneAmount + diamondAmount + preciousStoneAmount + otherMaterialAmount;
  
  // Net weight = metal weight only
  const netWeight = product.weight;
  
  // Gross weight = metal + all stones + other materials
  const grossWeight = netWeight + stoneWeight + diamondGrams + preciousStoneGrams + otherMaterialWeight;
  
  // Subtotal = Metal + Making Charges (after discount) + Extras
  const subtotal = basePrice + makingChargesAfterDiscount + totalExtras;
  
  // GST is calculated on subtotal (after discount on making charges)
  const originalGstAmount = subtotal * (fallbackRate.gst_percent / 100);
  const originalTotalPrice = subtotal + originalGstAmount;
  
  // Round total price: >= 0.5 rounds up, < 0.5 rounds down
  const roundedTotalPrice = Math.round(originalTotalPrice);
  
  // Back-calculate GST from rounded total
  // Formula: GST = RoundedTotal - (RoundedTotal / (1 + GST%/100))
  const gstMultiplier = 1 + (fallbackRate.gst_percent / 100); // e.g., 1.03 for 3% GST
  const newSubtotal = roundedTotalPrice / gstMultiplier;
  const newGstAmount = roundedTotalPrice - newSubtotal;
  
  // Calculate adjustment needed and apply to discount
  const subtotalDifference = newSubtotal - subtotal;
  const adjustedDiscountAmount = discountAmount - subtotalDifference;
  const adjustedMakingChargesAfterDiscount = makingCharges - adjustedDiscountAmount;

  return {
    basePrice: basePrice.toFixed(2),
    makingCharges: makingCharges.toFixed(2),
    makingChargesAfterDiscount: adjustedMakingChargesAfterDiscount.toFixed(2),
    makingChargesType: product.making_charges_type || 'percentage',
    makingChargesValue: product.making_charges_percent,
    // Stone
    stoneWeight: stoneWeight,
    stoneAmount: stoneAmount.toFixed(2),
    // Diamond
    diamondCents: diamondCents,
    diamondCarats: diamondCarats.toFixed(2),
    diamondGrams: diamondGrams.toFixed(3),
    diamondAmount: diamondAmount.toFixed(2),
    diamondQuality: diamondQuality,
    // Precious Stone
    preciousStoneCents: preciousStoneCents,
    preciousStoneCarats: preciousStoneCarats.toFixed(2),
    preciousStoneGrams: preciousStoneGrams.toFixed(3),
    preciousStoneAmount: preciousStoneAmount.toFixed(2),
    // Other Material
    otherMaterialType: otherMaterialType,
    otherMaterialWeight: otherMaterialWeight.toFixed(2),
    otherMaterialAmount: otherMaterialAmount.toFixed(2),
    // Weights
    netWeight: netWeight.toFixed(2),
    grossWeight: grossWeight.toFixed(3),
    // Subtotal (recalculated from rounded total)
    subtotal: newSubtotal.toFixed(2),
    // Discount (adjusted to balance rounding)
    discountPercent: discountPercent,
    discountAmount: adjustedDiscountAmount.toFixed(2),
    // GST (recalculated from rounded total)
    gstAmount: newGstAmount.toFixed(2),
    gstPercent: fallbackRate.gst_percent,
    // Final total (rounded to nearest whole number)
    totalPrice: roundedTotalPrice.toString(),
    // Rate used info
    rateUsed: fallbackRate.purity,
    ratePerGram: fallbackRate.rate_per_gram,
    // Legacy fields for backward compatibility
    stonePrice: totalExtras.toFixed(2),
    stoneCarat: diamondCarats,
  };
};